/**
 * Esquemas Zod del dominio de Estadísticas avanzadas.
 *
 * El dominio es de **lectura**: los únicos parámetros de "formulario" que viajan a
 * la API son los períodos de cada gráfica (`?period=`). Cada gráfica del SSR
 * (`advanced_stats_with_charts.html`) admite su propio juego de períodos; aquí se
 * valida con Zod que el valor sea uno de los admitidos por el backend
 * (`StatsService` / `domains/stats.py`), de modo que la pantalla nunca solicite un
 * período inválido. Los tipos se **infieren** del esquema (`z.infer`), no se
 * escriben a mano (`docs/frontend_conventions.md`, Validación).
 */
import { z } from "zod";

/**
 * Período del **panel global** y de la gráfica de órdenes (réplica de las pestañas
 * "Hoy / Semana / Mes" del SSR). Coincide con los valores que acepta
 * `StatsService.orders_chart` (`day` por defecto, `week`, `month`).
 */
export const ordersPeriodSchema = z.enum(["day", "week", "month"]);

/** Período del panel global / órdenes (inferido del esquema). */
export type OrdersPeriod = z.infer<typeof ordersPeriodSchema>;

/**
 * Período de la gráfica de **ingresos** (pestañas "Sem. / Mes / Año" del SSR).
 * Coincide con `StatsService.revenue_chart` (`week`, `month` por defecto, `year`).
 */
export const revenuePeriodSchema = z.enum(["week", "month", "year"]);

/** Período de ingresos (inferido del esquema). */
export type RevenuePeriod = z.infer<typeof revenuePeriodSchema>;

/**
 * Período de **ventas por vendedor** (pestañas "Día / Sem. / Mes / Año" del SSR).
 * Coincide con `StatsService.sales_by_seller` (`day`, `week` por defecto, `month`,
 * `year`).
 */
export const sellerPeriodSchema = z.enum(["day", "week", "month", "year"]);

/** Período de ventas por vendedor (inferido del esquema). */
export type SellerPeriod = z.infer<typeof sellerPeriodSchema>;
